import { profileId, useProfilesManaged } from "@lens-protocol/react-web";
import { useFetcher, useSearchParams } from "@remix-run/react";
import { track } from "@vercel/analytics";
import { useEffect } from "react";

import { Button } from "../Button";
import { cn } from "../utils";

export function LoginForm({ owner }: { owner: string }) {
  const [searchParams] = useSearchParams();
  const {
    data: profiles,
    error,
    loading,
  } = useProfilesManaged({ for: owner, includeOwned: true });
  const fetcher = useFetcher();

  useEffect(() => {
    if (!loading && profiles?.length === 0) {
      track("login_with_no_lens_profiles", { owner });
    }
  }, [profiles, loading]);

  useEffect(() => {
    if (error) {
      track("failed_lens_auth", { owner, error: error.message });
    }
  }, [error]);

  const onSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const form = event.currentTarget;
    const payloadFormData = new FormData(form);

    const id = profileId(payloadFormData.get("id") as string);

    const profile = profiles?.find((profile) => profile.id === id);

    if (!profile) {
      throw new Error("Profile not found");
    }

    const formData = new FormData();
    formData.append("id", profile.id);
    const shortHandle = profile.handle?.localName;
    if (shortHandle) {
      formData.append("username", shortHandle);
    }
    const displayName = profile.metadata?.displayName;
    if (displayName) {
      formData.append("displayName", displayName);
    }
    const bio = profile.metadata?.bio;
    if (bio) {
      formData.append("bio", bio);
    }
    // @ts-expect-error - it actually does exist
    const pfpUrl = profile.metadata?.picture?.optimized.uri;
    if (pfpUrl) {
      formData.append("pfpUrl", pfpUrl);
    }

    formData.append("ethAddress", owner);

    const redirect = searchParams.get("redirect") ?? "/";
    const action = `/api/auth/lens?redirect=${encodeURIComponent(redirect)}`;

    fetcher.submit(formData, { method: "POST", action });
  };

  if (loading) {
    return (
      <span className="text-white text-center w-full h-20">loading...</span>
    );
  }

  if (error) {
    return <span>Error: {error.message}</span>;
  }

  if (profiles.length === 0) {
    return null;
  }

  return (
    <form onSubmit={onSubmit} className="flex w-full">
      <fieldset className="flex place-items-center flex-col w-full">
        <div className="my-4 space-y-2">
          {profiles.map((profile, idx) => (
            <label
              key={profile.id}
              className={cn(
                "w-full items-center p-4 rounded-lg cursor-pointer border transition-colors border-gray-300 hover:border-gray-500 grid grid-cols-[24px_auto]",
                profiles.length === 1 && "hidden",
              )}
            >
              <input
                disabled={fetcher.state === "loading"}
                type="radio"
                defaultChecked={idx === 0}
                name="id"
                value={profile.id}
                className="box-content h-1.5 w-1.5 appearance-none rounded-full border-[5px] border-white bg-white bg-clip-padding outline-none ring-1 ring-gray-950/10 checked:border-green-500 checked:ring-green-500"
              />
              <span className="text-white text-sm font-semibold">
                {profile.handle?.fullHandle ?? profile.id}
              </span>
            </label>
          ))}
        </div>

        <Button
          disabled={fetcher.state === "loading"}
          type="submit"
          className="w-full"
        >
          {fetcher.state === "loading"
            ? "loading..."
            : profiles?.length === 1
              ? `login with ${profiles[0].handle?.fullHandle ?? profiles[0].id}`
              : "select profile"}
        </Button>
      </fieldset>
    </form>
  );
}
